import { Timestamp } from "firebase/firestore";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { createPlaceNote } from "../../services/placeNoteService";
import { Place } from "../../types/Place";
import { PlaceNoteDocument } from "../../types/PlaceNote";
import { UserProfile } from "../../types/User";
import { PublicUserProfileLink } from "../PublicUserProfileLink/PublicUserProfileLink";

interface NoteFormProps {
  place: Place;
  profile: UserProfile | null;
  disabled: boolean;
  onAddNote: () => void;
}

export const NoteForm: React.FC<NoteFormProps> = ({
  place,
  profile,
  disabled,
  onAddNote,
}) => {
  const [note, setNote] = useState("");
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const loggedIn = !!profile;

  const handleFocus = useCallback(() => {
    if (profile) {
      setFocused(true);
    } else {
      toast.error("You must be logged in to add a note!");
    }
  }, [profile]);

  const handleSubmitNote = useCallback(async () => {
    if (note.length < 5) {
      return toast.error("Write a bit more!");
    }

    if (!profile) {
      return toast.error("You must be logged in to add a note!");
    }

    const placeNoteDocument: Partial<PlaceNoteDocument> = {
      placeId: place.googlePlaceId,
      placeName: place.name,
      creatorId: profile.userId,
      creatorUsername: profile.username,
      creatorProfilePictureUrl: profile.photoURL,
      noteText: note,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      upvotes: 1,
      downvotes: 0,
      voterUserIds: [profile.userId],
    };

    setLoading(true);
    try {
      await createPlaceNote(placeNoteDocument);
      toast.success("Thanks for your note!");
      setNote("");
      onAddNote();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [note, place, profile, onAddNote]);

  return (
    <div className="note-form-container">
      <div className="faux-form-control">
        <div className="faux-form-control-input">
          <textarea
            name="note"
            placeholder={loggedIn ? "Add a note" : "Please login to add a note"}
            value={note}
            rows={focused || note.length > 0 ? 5 : 2}
            onFocus={handleFocus}
            onBlur={() => setFocused(false)}
            onChange={(e) => setNote(e.target.value)}
            disabled={disabled}
          />
        </div>
        <div className="faux-form-control-actions">
          {profile && (
            <PublicUserProfileLink
              profile={profile}
              size="sm"
              linkToProfile={false}
            />
          )}
          <button
            disabled={disabled || loading || !loggedIn}
            className="btn btn-primary btn-sm"
            onClick={handleSubmitNote}
          >
            Add note
          </button>
        </div>
      </div>
    </div>
  );
};
